// All content, trademarks, and data on this document are the property of Healthworks Analytics, LLC and are protected by applicable intellectual property laws. Unauthorized use, reproduction, or distribution of this material is strictly prohibited.
import { memo, useEffect } from "react";

import logo from "../../img/logo.svg";
import { useDispatch, useSelector } from "react-redux";
import ExpiredSession from "../../views/ExpiredSession";
import { useState } from "react";

import { resetPassword, verifySession } from "../../services/auth.service";
import { loginFailure } from "../../store/auth/action";

import { useNavigate, useParams } from "react-router-dom";
import StartLoader from "../loaders/StartLoader";
import ActionError from "../errors/ActionError";
import {
  AiOutlineCheck,
  AiOutlineEye,
  AiOutlineEyeInvisible,
} from "react-icons/ai";
import { checkPasswordStrength } from "../../utils/functions";
import AuthError from "../errors/AuthError";

// The API can crash and return a raw "Server Error: ..." stack message rather
// than a clean response, so fall back to our own copy instead of rendering
// server internals to the user.
const friendlyError = (err, fallback) => {
  const serverMessage = err?.response?.data;
  return typeof serverMessage === "string" &&
    serverMessage.trim() !== "" &&
    !serverMessage.startsWith("Server Error:")
    ? serverMessage
    : fallback;
};

const PasswordRule = ({ met, label }) => (
  <div className="self-stretch flex justify-start items-center gap-[7.2px]">
    <div className="w-[12.6px] h-[12.6px] shrink-0 flex justify-center items-center">
      <AiOutlineCheck
        color="#28A745"
        size={10.8}
        className={`${met ? "" : "opacity-0"} ani`}
      />
    </div>
    <div
      className={`${
        met ? "text-success" : "text-unofficial-ghost-foreground"
      } ani text-[12.6px] font-normal leading-[18px]`}
    >
      {label}
    </div>
  </div>
);

const ResetPassword = () => {
  const { error } = useSelector((state) => ({
    error: state.error,
  }));
  const { isLoading, failure } = useSelector((state) => ({
    isLoading: state.isLoading,
    failure: state.failure,
  }));
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const { session_id } = useParams();
  const [sessionExist, setSessionExist] = useState(true);
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");

  const [apiLoad, setApiLoad] = useState(false);
  const [passwordPolicy, setPasswordPolicy] = useState(null);

  const [errorText, setErrorText] = useState("Link has expired.");

  const [passwordChanged, setPasswordChanged] = useState(false);

  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [passwordTouched, setPasswordTouched] = useState(false);
  const [confirmTouched, setConfirmTouched] = useState(false);

  const strength = checkPasswordStrength(password);
  const passwordError = passwordTouched
    ? password.length === 0
      ? "Password required"
      : !strength.validLength
      ? "Must be at least 8 characters long"
      : ""
    : "";
  const confirmError = confirmTouched
    ? confirmPassword.length === 0
      ? "Confirm Password required"
      : confirmPassword !== password
      ? "Passwords don't match"
      : ""
    : "";
  const isSubmitDisabled =
    (passwordPolicy?.level !== "normal" && strength.stregthLevel !== 5) ||
    password !== confirmPassword ||
    password.trim() === "" ||
    !strength.validLength;

  async function sessionVerification() {
    setApiLoad(true);
    try {
      const res = await verifySession({ session_id: session_id });
      // console.log(res, "res");
      if (res.isValid !== true) {
        setErrorText("Link has expired.");
        setSessionExist(false);
      } else {
        setPasswordPolicy(res.passwordPolicy);
      }
    } catch (err) {
      console.log(err, "error");
      // A "Server Error: ... (reading 'token')" here means the session_id has
      // no live record, i.e. the link is spent.
      setErrorText(friendlyError(err, "Link has expired."));
      setSessionExist(false);
    }
    setApiLoad(false);
  }

  useEffect(() => {
    sessionVerification();
  }, [session_id]);

  async function SaveDetails() {
    setApiLoad(true);
    try {
      const payload = { password: password, confirmPassword: confirmPassword };

      await resetPassword(payload);
      setPasswordChanged(true);
    } catch (err) {
      console.log(err, "error");

      const serverMessage = err?.response?.data;
      if (
        typeof serverMessage === "string" &&
        serverMessage.includes("passwords cannot be reused")
      ) {
        dispatch(loginFailure(serverMessage));
      } else {
        setErrorText(
          friendlyError(err, "Something went wrong. Contact support.")
        );
        setSessionExist(false);
      }
    }
    setApiLoad(false);
  }

  return (
    <>
      {error.length > 0 && <AuthError error={error} />}

      {!sessionExist ? (
        <ExpiredSession text={errorText} />
      ) : passwordChanged ? (
        <div className="font-geist h-screen w-screen px-[21.6px] py-[21.6px] bg-general-muted flex flex-col justify-center items-center">
          <div className="p-[28.8px] bg-general-background rounded-[7.2px] shadow-[0px_1px_2px_0px_rgba(0,0,0,0.05)] flex flex-col justify-start items-center gap-[21.6px]">
            <div className="w-[129.6px] h-[18px] overflow-hidden">
              <img alt="logo" src={logo} className="w-[129.6px] h-[18px] object-contain" />
            </div>

            <div className="w-72 text-center text-general-foreground text-[21.6px] font-semibold leading-[28.8px]">
              Password Changed
            </div>

            <button
              type="button"
              className="text-center text-unofficial-ghost-foreground text-[12.6px] font-normal leading-[18px] hover:underline"
              onClick={() => navigate("/login")}
            >
              Log In
            </button>
          </div>
        </div>
      ) : (
        <div className="font-geist h-screen w-screen px-[21.6px] py-[21.6px] bg-general-muted flex flex-col justify-center items-center">
          <div className="p-[28.8px] bg-general-background rounded-[7.2px] shadow-[0px_1px_2px_0px_rgba(0,0,0,0.05)] flex flex-col justify-start items-center gap-[28.8px]">
            <div className="w-[129.6px] h-[18px] overflow-hidden">
              <img alt="logo" src={logo} className="w-[129.6px] h-[18px] object-contain" />
            </div>

            <div className="w-72 flex flex-col justify-start items-center gap-[14.4px]">
              <div className="text-center text-general-foreground text-[21.6px] font-semibold leading-[28.8px]">
                Create New Password
              </div>
              <div className="text-center text-general-muted-foreground text-[12.6px] font-normal leading-[18px]">
                For security reasons, you will be logged out of all devices
                after password is changed.
              </div>
            </div>

            {/* form starts */}
            <div className="w-72 flex flex-col justify-start items-start gap-[14.4px]">
              <div className="self-stretch flex flex-col justify-start items-start gap-[7.2px]">
                <div className="self-stretch flex flex-col justify-start items-start gap-[3.6px]">
                  <div className="text-general-foreground text-[12.6px] font-normal leading-[18px]">
                    Enter Password
                  </div>
                  <div
                    className={`${
                      passwordError
                        ? "outline-unofficial-destructive-border"
                        : "outline-general-border shadow-[0px_1px_2px_0px_rgba(0,0,0,0.05)] focus-within:outline-unofficial-border-3 focus-within:shadow-[0px_0px_0px_2.7px_rgba(212,212,212,1.00)]"
                    } self-stretch min-h-[32.4px] px-[10.8px] py-[7.2px] bg-general-input rounded-[7.2px] outline outline-1 outline-offset-[-1px] flex justify-start items-center gap-[7.2px] overflow-hidden`}
                  >
                    <input
                      disabled={apiLoad}
                      value={password}
                      placeholder="Enter Password"
                      type={showPassword ? "text" : "password"}
                      id="npassword"
                      name="npassword"
                      className="flex-1 text-general-foreground text-[12.6px] font-normal leading-[18px] outline-none placeholder-general-border bg-general-input"
                      onChange={(e) => {
                        setPassword(e.target.value);
                      }}
                      onBlur={() => setPasswordTouched(true)}
                      autoComplete="off"
                    />
                    <button
                      type="button"
                      className="w-[18px] p-[1.8px] flex flex-col justify-center items-center"
                      onClick={() => setShowPassword(!showPassword)}
                    >
                      {showPassword ? (
                        <AiOutlineEye color="#737373" size={14.4} />
                      ) : (
                        <AiOutlineEyeInvisible color="#737373" size={14.4} />
                      )}
                    </button>
                  </div>
                </div>

                {passwordError && (
                  <div className="self-stretch text-left text-unofficial-destructive-text text-[12.6px] font-normal leading-[18px]">
                    {passwordError}
                  </div>
                )}
              </div>

              <div className="self-stretch flex flex-col justify-start items-start gap-[7.2px]">
                <div className="self-stretch flex flex-col justify-start items-start gap-[3.6px]">
                  <div className="text-general-foreground text-[12.6px] font-normal leading-[18px]">
                    Confirm Password
                  </div>
                  <div
                    className={`${
                      confirmError
                        ? "outline-unofficial-destructive-border"
                        : "outline-general-border shadow-[0px_1px_2px_0px_rgba(0,0,0,0.05)] focus-within:outline-unofficial-border-3 focus-within:shadow-[0px_0px_0px_2.7px_rgba(212,212,212,1.00)]"
                    } self-stretch min-h-[32.4px] px-[10.8px] py-[7.2px] bg-general-input rounded-[7.2px] outline outline-1 outline-offset-[-1px] flex justify-start items-center gap-[7.2px] overflow-hidden`}
                  >
                    <input
                      disabled={apiLoad}
                      value={confirmPassword}
                      placeholder="Confirm Password"
                      type={showConfirmPassword ? "text" : "password"}
                      id="cpassword"
                      name="cpassword"
                      className="flex-1 text-general-foreground text-[12.6px] font-normal leading-[18px] outline-none placeholder-general-border bg-general-input"
                      onChange={(e) => {
                        setConfirmPassword(e.target.value);
                      }}
                      onBlur={() => setConfirmTouched(true)}
                      autoComplete="off"
                    />
                    <button
                      type="button"
                      className="w-[18px] p-[1.8px] flex flex-col justify-center items-center"
                      onClick={() =>
                        setShowConfirmPassword(!showConfirmPassword)
                      }
                    >
                      {showConfirmPassword ? (
                        <AiOutlineEye color="#737373" size={14.4} />
                      ) : (
                        <AiOutlineEyeInvisible color="#737373" size={14.4} />
                      )}
                    </button>
                  </div>
                </div>

                {confirmError && (
                  <div className="self-stretch text-left text-unofficial-destructive-text text-[12.6px] font-normal leading-[18px]">
                    {confirmError}
                  </div>
                )}
              </div>

              <button
                type="submit"
                className={`${
                  isSubmitDisabled || apiLoad
                    ? "opacity-50 cursor-not-allowed"
                    : "hover:bg-hoverPrimary"
                } self-stretch min-h-[32.4px] px-[14.4px] py-[7.2px] bg-general-primary rounded-[7.2px] flex justify-center items-center gap-[7.2px]`}
                onClick={() => SaveDetails()}
                disabled={isSubmitDisabled || apiLoad}
              >
                <div className="text-center text-general-primary-foreground text-[12.6px] font-medium leading-[18px]">
                  Create Password
                </div>
              </button>

              <div className="self-stretch flex flex-col justify-start items-start gap-[7.2px] select-none">
                <div className="text-general-foreground text-[12.6px] font-normal leading-[18px]">
                  Password must contain at least:
                </div>
                <PasswordRule met={strength.validLength} label="8 characters" />
                <PasswordRule
                  met={strength.hasLowercase}
                  label="One lowercase letter"
                />
                <PasswordRule
                  met={strength.hasUppercase}
                  label="One capital letter"
                />
                <PasswordRule met={strength.hasNumber} label="One number" />
                <PasswordRule met={strength.hasSpecialChar} label="One symbol" />
              </div>
            </div>
            {/*form ends  */}
          </div>
        </div>
      )}
      {failure && <ActionError errorMsg="Something Went Wrong" />}
      {isLoading && <StartLoader />}
    </>
  );
};

export default memo(ResetPassword);
